import type {
    BatchCounts,
    BatchHandle,
    BatchReadiness,
    BatchTrackingPhase,
    TrackedBatch
} from "./types";

export type BatchRegistryListener = (batches: TrackedBatch[]) => void;

const emptyCounts = (): BatchCounts => ({
    pending: 0,
    queued: 0,
    running: 0,
    completed: 0,
    failed: 0,
    cancelled: 0,
    total: 0
});

const terminal = new Set<BatchTrackingPhase>([
    "completed",
    "partial_failure",
    "failed",
    "cancelled",
    "blocked"
]);

function phaseFor(handle: BatchHandle): BatchTrackingPhase {
    switch (handle.status) {
        case "planned": return "queued";
        case "queued": return "queued";
        case "running": return "running";
        case "cancelling": return "cancelling";
        case "completed": return "completed";
        case "partial_failure": return "partial_failure";
        case "failed": return "failed";
        case "cancelled": return "cancelled";
        default: return handle.counts.running > 0 ? "running" : "queued";
    }
}

function messageFor(handle: BatchHandle, phase: BatchTrackingPhase): string {
    const counts = handle.counts;
    const latest = handle.recentCompletions.at(0);
    if (phase === "completed") {
        return counts.total === 0
            ? "All Work Products are already current."
            : `All ${counts.total} Work Products completed.`;
    }
    if (phase === "partial_failure") {
        const cancelled = counts.cancelled > 0 ? `; ${counts.cancelled} cancelled` : "";
        return `${counts.completed} completed; ${counts.failed} failed${cancelled}.`;
    }
    if (phase === "failed") return `Batch generation failed for ${counts.failed} Work Products.`;
    if (phase === "cancelled") {
        return `Batch cancelled; ${counts.completed} completed and ${counts.cancelled} cancelled.`;
    }
    if (phase === "cancelling") return "Stopping future work and cancelling submitted jobs…";
    if (latest) return `${latest} created (${counts.completed}/${counts.total})…`;
    return `${counts.pending} pending, ${counts.queued} queued, ${counts.running} running.`;
}

export class BatchGenerationRegistry {
    private readonly entries = new Map<string, TrackedBatch>();
    private readonly trackingByBatchId = new Map<string, string>();
    private readonly listeners = new Set<BatchRegistryListener>();
    private readonly pendingChat: string[] = [];
    private sequence = 0;

    acknowledgeChatPrompt(prompt: string): string {
        const trackingId = `chat-${Date.now()}-${this.sequence++}`;
        this.entries.set(trackingId, {
            trackingId,
            prompt,
            phase: "checking",
            message: "Checking Context Mesh…",
            counts: emptyCounts(),
            completedLabels: [],
            createdAt: new Date().toISOString()
        });
        this.pendingChat.push(trackingId);
        this.emit();
        return trackingId;
    }

    setReadiness(readiness: BatchReadiness): void {
        const trackingId = this.pendingChat[0];
        if (!trackingId) return;
        const current = this.entries.get(trackingId);
        if (!current) return;
        if (!readiness.ready) {
            this.pendingChat.shift();
            this.entries.set(trackingId, {
                ...current,
                readiness,
                phase: "blocked",
                message: "Batch generation is blocked until the missing Context Mesh inputs are completed."
            });
        } else {
            this.entries.set(trackingId, {
                ...current,
                readiness,
                phase: "ready",
                message: "Context Mesh is ready. Starting batch generation…"
            });
        }
        this.emit();
    }

    registerBatch(handle: BatchHandle): void {
        const pendingId = handle.origin === "chat" ? this.pendingChat.shift() : undefined;
        const knownId = this.trackingByBatchId.get(handle.batchId);
        const trackingId = pendingId || knownId || handle.batchId;
        const previous = this.entries.get(pendingId || knownId || trackingId);
        if (pendingId && knownId && pendingId !== knownId) this.entries.delete(knownId);
        this.trackingByBatchId.set(handle.batchId, trackingId);
        this.store(trackingId, handle, previous);
        this.emit();
    }

    syncBatch(handle: BatchHandle): void {
        const trackingId = this.trackingByBatchId.get(handle.batchId) || handle.batchId;
        const previous = this.entries.get(trackingId);
        if (
            previous?.handle &&
            Date.parse(handle.updatedAt) < Date.parse(previous.handle.updatedAt)
        ) return;
        if (previous && terminal.has(previous.phase) && !terminal.has(phaseFor(handle))) return;
        this.trackingByBatchId.set(handle.batchId, trackingId);
        this.store(trackingId, handle, previous);
        this.emit();
    }

    activeBatchIds(): string[] {
        return [...this.entries.values()]
            .filter(entry => entry.handle && !terminal.has(entry.phase))
            .map(entry => entry.handle!.batchId);
    }

    failPendingChat(message: string): void {
        const trackingId = this.pendingChat.shift();
        if (!trackingId) return;
        const current = this.entries.get(trackingId);
        if (!current) return;
        this.entries.set(trackingId, { ...current, phase: "failed", message });
        this.emit();
    }

    clearFinished(): void {
        for (const [trackingId, entry] of this.entries) {
            if (!terminal.has(entry.phase)) continue;
            this.entries.delete(trackingId);
            if (entry.handle) this.trackingByBatchId.delete(entry.handle.batchId);
        }
        this.emit();
    }

    subscribe(listener: BatchRegistryListener): () => void {
        this.listeners.add(listener);
        listener(this.replayable());
        return () => this.listeners.delete(listener);
    }

    private store(trackingId: string, handle: BatchHandle, previous?: TrackedBatch): void {
        const phase = phaseFor(handle);
        this.entries.set(trackingId, {
            trackingId,
            prompt: previous?.prompt,
            readiness: previous?.readiness,
            handle,
            phase,
            message: messageFor(handle, phase),
            counts: { ...handle.counts },
            completedLabels: [...handle.recentCompletions],
            createdAt: previous?.createdAt || handle.createdAt
        });
    }

    private replayable(): TrackedBatch[] {
        return [...this.entries.values()].filter(entry => !terminal.has(entry.phase));
    }

    private emit(): void {
        const snapshot = [...this.entries.values()];
        this.listeners.forEach(listener => listener(snapshot));
    }
}

export const createBatchGenerationRegistry = (): BatchGenerationRegistry =>
    new BatchGenerationRegistry();
